import { CheckCircle2, Eye, FileCheck, MessageSquare, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CollaborationStatusKey } from '@/lib/section-status'

/** Sentence-case labels (spec 29 §0.6.7). The picker config keeps its own. */
export const SECTION_STATUS_LABELS: Record<CollaborationStatusKey, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  draft_complete: 'Draft complete',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  complete: 'Complete',
}

type Glyph = 'dot' | 'file-check' | 'eye' | 'message' | 'shield' | 'check'

// shield = live reviewer sign-off, tick = completed, eye = in review.
const GLYPH: Record<CollaborationStatusKey, Glyph> = {
  not_started: 'dot', in_progress: 'dot', draft_complete: 'file-check',
  in_review: 'eye', changes_requested: 'message', approved: 'shield', complete: 'check',
}

const TONE: Record<CollaborationStatusKey, string> = {
  not_started: 'bg-muted border-transparent text-muted-foreground',
  in_progress: 'bg-[hsl(var(--status-warning)/0.12)] border-[hsl(var(--status-warning)/0.24)] text-[hsl(var(--status-warning))]',
  draft_complete: 'bg-[hsl(var(--status-info)/0.12)] border-[hsl(var(--status-info)/0.22)] text-[hsl(var(--status-info))]',
  in_review: 'bg-[hsl(var(--status-review)/0.12)] border-[hsl(var(--status-review)/0.22)] text-[hsl(var(--status-review))]',
  changes_requested: 'bg-destructive/10 border-destructive/20 text-destructive',
  approved: 'bg-[hsl(var(--status-success)/0.17)] border-[hsl(var(--status-success)/0.38)] text-[hsl(var(--status-success))]',
  complete: 'bg-[hsl(var(--status-success)/0.12)] border-[hsl(var(--status-success)/0.22)] text-[hsl(var(--status-success))]',
}

function GlyphIcon({ glyph }: { glyph: Glyph }) {
  const cls = 'h-3 w-3 shrink-0'
  switch (glyph) {
    case 'file-check': return <FileCheck className={cls} />
    case 'eye': return <Eye className={cls} />
    case 'message': return <MessageSquare className={cls} />
    case 'shield': return <ShieldCheck className={cls} />
    case 'check': return <CheckCircle2 className={cls} />
    default: return <span className="h-1.5 w-1.5 rounded-full bg-current shrink-0" />
  }
}

export function SectionStatusPill({ status, className }: { status: CollaborationStatusKey | null | undefined; className?: string }) {
  const key: CollaborationStatusKey = status ?? 'not_started'
  const glyph = GLYPH[key]
  return (
    <span
      data-glyph={glyph}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE[key],
        className,
      )}
    >
      <GlyphIcon glyph={glyph} />
      {SECTION_STATUS_LABELS[key]}
    </span>
  )
}
